import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as api from './api';
import type {
  CreatePupilInput,
  CreateParentLinkInput,
  MemberListParams,
  MemberUpdateInput,
  MessageableMemberParams,
  ParentLinkListParams,
} from './types';

/** Query-key factory — every members key derives from here. */
export const memberKeys = {
  all: ['members'] as const,
  lists: () => [...memberKeys.all, 'list'] as const,
  list: (params: MemberListParams) => [...memberKeys.lists(), params] as const,
  messageable: (params: MessageableMemberParams) =>
    [...memberKeys.all, 'messageable', params] as const,
};

export function useMembers(params: MemberListParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: memberKeys.list(params),
    queryFn: () => api.getMembers(params),
    // Keep the previous page on screen while the next one loads (no flicker
    // when paginating or typing a search).
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

/**
 * The caller's own DM candidates — readable by every role, unlike `useMembers`.
 * Search is debounced by the caller and travels to the server (the set can be
 * a whole school's staff, so it is not filtered in memory).
 */
export function useMessageableMembers(
  params: MessageableMemberParams = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: memberKeys.messageable(params),
    queryFn: () => api.getMessageableMembers(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

/** Query-key factory for parent↔child links. */
export const parentLinkKeys = {
  all: ['parent-links'] as const,
  lists: () => [...parentLinkKeys.all, 'list'] as const,
  list: (params: ParentLinkListParams) => [...parentLinkKeys.lists(), params] as const,
};

export function useParentLinks(
  params: ParentLinkListParams = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: parentLinkKeys.list(params),
    queryFn: () => api.getParentLinks(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

export function useCreateParentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateParentLinkInput) => api.createParentLink(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: parentLinkKeys.lists() }),
  });
}

/**
 * Remove a link. It closes the parent's access — and, since ADR-026, the DM
 * matrix is re-derived on every post, so it also closes the conversation. The
 * pupil's own history is untouched.
 */
export function useDeleteParentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.deleteParentLink(publicId),
    onSuccess: () => qc.invalidateQueries({ queryKey: parentLinkKeys.lists() }),
  });
}

/** The password rides only in the mutation call — never into the query cache
 *  (the RevealedKeyPanel rule, ADR-038). */
export function useCreatePupil() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePupilInput) => api.createPupil(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: memberKeys.lists() }),
  });
}

export function useSetMemberPassword() {
  return useMutation({
    mutationFn: ({ publicId, password }: { publicId: string; password: string }) =>
      api.setMemberPassword(publicId, password),
  });
}

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, input }: { publicId: string; input: MemberUpdateInput }) =>
      api.updateMember(publicId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: memberKeys.lists() }),
  });
}
